import React from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Linkedin, Instagram, Mail, MapPin, Globe } from 'lucide-react';

const Footer = ({ settings = {} }) => {
  const currentYear = new Date().getFullYear();
  
  // Logo URL
  const LOGO_URL = '/michezonet-logo.png';

  const footerLinks = {
    services: [
      { name: 'Sports Agency', href: '/services#agency' },
      { name: 'Sports Technology', href: '/services#technology' },
      { name: 'Governance & Advisory', href: '/services#governance' },
      { name: 'Commercial & Sponsorship', href: '/services#commercial' },
      { name: 'Events & Operations', href: '/services#events' }
    ],
    technology: [
      { name: 'MyGolfHub', href: '/platform#mygolfhub' },
      { name: 'Platform', href: '/platform' },
      { name: 'Sports Directory', href: '/directory' }
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Programs', href: '/programs' },
      { name: 'Insights', href: '/insights' },
      { name: 'Contact', href: '/contact' },
      { name: 'Careers', href: '/jobs' }
    ],
    resources: [
      { name: 'Events', href: '/events' },
      { name: 'Road to SomaBay', href: '/somabay' },
      { name: 'Login', href: '/login' },
      { name: 'Dashboard', href: '/dashboard' }
    ]
  };

  return (
    <footer className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src={LOGO_URL} 
                alt="Michezonet - Your Passion is Our Business" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-2 font-semibold">
              Africa's Integrated Sports Agency & Business Solutions Company
            </p>
            <p className="text-white/40 text-xs leading-relaxed mb-6">
              Sports Agency • Technology • Governance • Commercial Solutions
            </p>
            <div className="flex gap-3">
              <a href="https://twitter.com/michezonet" target="_blank" rel="noopener noreferrer" 
                 className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center text-white/60 hover:text-[#008000] hover:bg-white/10 transition-colors">
                <Twitter size={16} />
              </a>
              <a href="https://linkedin.com/company/michezonet" target="_blank" rel="noopener noreferrer"
                 className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center text-white/60 hover:text-[#008000] hover:bg-white/10 transition-colors">
                <Linkedin size={16} />
              </a>
              <a href="https://instagram.com/michezonet" target="_blank" rel="noopener noreferrer"
                 className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center text-white/60 hover:text-[#008000] hover:bg-white/10 transition-colors">
                <Instagram size={16} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-heading font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/60 text-sm hover:text-[#008000] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & Technology */}
          <div>
            <h4 className="text-white font-heading font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/60 text-sm hover:text-[#008000] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-white font-heading font-semibold mb-3 mt-6 text-sm uppercase tracking-wider">Technology</h4>
            <ul className="space-y-2">
              {footerLinks.technology.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/60 text-sm hover:text-[#008000] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-heading font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-white/60 text-sm">
                <Mail size={14} className="text-[#008000]" />
                <a href="mailto:info@michezonet.co.ke" className="hover:text-[#008000] transition-colors">
                  info@michezonet.co.ke
                </a>
              </li>
              <li className="flex items-center gap-2 text-white/60 text-sm">
                <Globe size={14} className="text-[#008000]" />
                <a href="https://www.michezonet.co.ke" target="_blank" rel="noopener noreferrer" className="hover:text-[#008000] transition-colors">
                  www.michezonet.co.ke
                </a>
              </li>
              <li className="flex items-center gap-2 text-white/60 text-sm">
                <MapPin size={14} className="text-[#008000]" />
                <span>Nairobi, Kenya</span>
              </li>
            </ul>
            <h4 className="text-white font-heading font-semibold mb-3 mt-6 text-sm uppercase tracking-wider">Resources</h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/60 text-sm hover:text-[#008000] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} Michezonet. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 text-sm hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/40 text-sm hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
